/**
 * The Meridian-derived Executive board: pure logic.
 *
 * A tenant with no executive workbook (public.exec_kpi, MER-667) gets its
 * Executive page from public.exec_derived (migration 0085), which the
 * pipeline builds from the tenant's own imports (audit/exec_derived.py): one
 * row per sheet x line x month, shaped like an exec_kpi row. This module maps
 * those rows onto the exec_kpi row shape so the page's own grouping, cards,
 * trend, table and CSV (execKpi.mjs) render them with the identical layout.
 *
 * Rules it keeps:
 *   - Every line is Meridian's figure, labelled "From your imports". None of
 *     these rows is a client figure, and none is ever written to exec_kpi
 *     (MER-717).
 *   - A month with no figure keeps the pipeline's reason (awaiting_reason)
 *     and shows no number. Nothing here fills a gap or computes a value.
 *   - The page's latest month is the pipeline's last complete month
 *     (latest_complete_period), so a vs-prior change compares two complete
 *     months (MER-553 / MER-693).
 *
 * AJ 2026-09-30: a tenant WITH a workbook (Delta) can take the same page
 * (dashboard_overrides `executive_page: derived`). The pipeline then builds
 * exec_derived over the workbook's own sheets and lines
 * (audit/exec_derived_workbook.py). Migration 0093 adds, per line, its state
 * (live, awaiting, definition_differs) and the source an awaiting line waits
 * for, which the page prints as "Awaiting <source>", "Not yet mapped" or
 * "Definition differs". The client's workbook figure is never on this page;
 * staff read it in the variance register, beside the page's figure.
 *
 * Plain .mjs so node:test covers it without React (the execKpi.mjs precedent).
 */

export const EXEC_DERIVED_TABLE = "exec_derived";

/**
 * The columns migration 0085 created, which the page reads. A database that
 * has not had 0093 applied yet is read with this list (the page then shows
 * the plain "Awaiting source data" label, exactly as before 0093).
 */
export const EXEC_DERIVED_COLUMNS_0085 = [
  "sheet",
  "sheet_label",
  "sheet_order",
  "division",
  "equipment_type",
  "is_rollup",
  "block",
  "line_item",
  "line_order",
  "period",
  "value",
  "unit",
  "decimals",
  "additive",
  "provenance",
  "hero_role",
  "note",
  "awaiting_reason",
  "latest_complete_period",
  "computed_at",
].join(",");

/** The columns the page reads: 0085's plus 0093's line state and awaited source. */
export const EXEC_DERIVED_COLUMNS = [
  EXEC_DERIVED_COLUMNS_0085,
  "line_state",
  "awaiting_source",
].join(",");

/**
 * The columns the staff variance register reads: the page's figure and state
 * per line and month, and the reason a line is not live. Staff only in use;
 * the table itself carries no client figure.
 */
export const EXEC_DERIVED_REGISTER_COLUMNS = [
  "sheet",
  "line_item",
  "period",
  "value",
  "unit",
  "decimals",
  "awaiting_reason",
  "line_state",
  "awaiting_source",
  "reason_code",
  "reason_detail",
].join(",");

/** Line states (migration 0093 CHECK; audit/exec_derived.LINE_STATES). */
export const LINE_LIVE = "live";
export const LINE_AWAITING = "awaiting";
export const LINE_DEFINITION_DIFFERS = "definition_differs";
export const LINE_STATES = Object.freeze([LINE_LIVE, LINE_AWAITING, LINE_DEFINITION_DIFFERS]);

/** The awaited source that means nothing names an input we lack. */
export const SOURCE_NOT_MAPPED = "not yet mapped";
export const NOT_MAPPED_LABEL = "Not yet mapped";
export const DEFINITION_DIFFERS_LABEL = "Definition differs";

export const DERIVED_PROVENANCE = "meridian_derived";

/** The page's provenance stamp for a derived board (the workbook page says "workbook received"). */
export const DERIVED_STAMP = "From your imports";

/** Copy on the awaiting card when the tenant has no derived rows yet. */
export const AWAITING_IMPORTS_COPY =
  "Awaiting your imports: the Executive page fills in after the next refresh";

/** Copy on the error card when the derived read fails. No database text reaches the page. */
export const DERIVED_UNAVAILABLE_COPY =
  "The executive page could not be loaded right now. Try again in a few minutes.";

/** Copy on a card whose line holds no figure yet. */
export const AWAITING_CARD_COPY = "Awaiting source data";

function toNumber(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

function text(v) {
  return typeof v === "string" && v.trim() !== "" ? v : null;
}

/**
 * The page's short words for a line that shows no figure: "Definition
 * differs", "Not yet mapped" or "Awaiting <source>". Null for a live line and
 * for a row written before 0093 (the page then says AWAITING_CARD_COPY).
 */
export function derivedLineLabel(lineState, awaitingSource) {
  if (lineState === LINE_DEFINITION_DIFFERS) return DEFINITION_DIFFERS_LABEL;
  if (lineState === LINE_LIVE) return null;
  const source = text(awaitingSource);
  if (source === null) return null;
  if (source.trim().toLowerCase() === SOURCE_NOT_MAPPED) return NOT_MAPPED_LABEL;
  return `Awaiting ${source.trim()}`;
}

/**
 * One sentence for the trend panel of a line with no figure. The label is
 * said once: the reason is left out when it only repeats the label, and the
 * label is left out when the reason already opens with it.
 */
export function awaitingSentence(lineItem, label, reason) {
  const head = label ?? AWAITING_CARD_COPY;
  if (!reason || reason === label) return `${lineItem}: ${head}.`;
  if (label && reason.startsWith(label)) return `${lineItem}: ${reason}.`;
  return `${head} for ${lineItem}: ${reason}.`;
}

/**
 * public.exec_derived rows -> exec_kpi-shaped rows the page groups. Returns
 * new rows; inputs are not mutated. A row without a sheet, line or period is
 * dropped, as groupRows would drop it.
 */
export function derivedToExecRows(rows) {
  const out = [];
  for (const row of rows ?? []) {
    if (!row || typeof row.sheet !== "string" || typeof row.line_item !== "string") continue;
    if (typeof row.period !== "string") continue;
    const value = toNumber(row.value);
    const reason =
      value === null ? (text(row.awaiting_reason) ?? "No figure for this month") : null;
    out.push({
      sheet: row.sheet,
      sheet_label: text(row.sheet_label),
      sheet_order: toNumber(row.sheet_order),
      division: text(row.division),
      equipment_type: text(row.equipment_type),
      is_rollup: row.is_rollup === true,
      block: row.block ?? null,
      line_item: row.line_item,
      line_order: toNumber(row.line_order),
      period: row.period,
      value,
      cell_text: null,
      unit: row.unit ?? null,
      decimals: toNumber(row.decimals),
      additive: row.additive === true,
      provenance: DERIVED_PROVENANCE,
      note: text(row.note),
      workbook_latest_period: text(row.latest_complete_period),
      created_date: null,
      hero_role: text(row.hero_role),
      source: "ours",
      source_reason: null,
      workbook_value: null,
      workbook_cell_text: null,
      missing_reason: reason,
      line_state: text(row.line_state),
      awaiting_label: derivedLineLabel(row.line_state, row.awaiting_source),
    });
  }
  return out;
}

/** The newest computed_at on the rows ('YYYY-MM-DD...'), or null. */
export function derivedComputedAt(rows) {
  let latest = null;
  for (const row of rows ?? []) {
    const t = text(row?.computed_at);
    if (t && (latest === null || t > latest)) latest = t;
  }
  return latest;
}

/* ------------------------------------------------------------------ */
/* The staff register's view of the derived page                      */
/* ------------------------------------------------------------------ */

function key(sheet, label) {
  return `${sheet}\u0000${label}`;
}

/** The index key for a sheet and line. */
export function derivedLineKey(sheet, label) {
  return key(sheet, label);
}

/**
 * exec_derived rows (EXEC_DERIVED_REGISTER_COLUMNS) indexed by line: its
 * state, the source it waits for, the reason it is not live, and the page's
 * figure per month. A line whose months disagree on the state takes the
 * first one read (the pipeline writes one state per line).
 */
export function indexDerivedLines(rows) {
  const index = new Map();
  for (const row of rows ?? []) {
    if (!row || typeof row.sheet !== "string" || typeof row.line_item !== "string") continue;
    if (typeof row.period !== "string") continue;
    const k = key(row.sheet, row.line_item);
    let entry = index.get(k);
    if (!entry) {
      entry = {
        state: text(row.line_state),
        source: text(row.awaiting_source),
        reasonCode: null,
        reasonDetail: null,
        unit: text(row.unit),
        decimals: toNumber(row.decimals),
        byPeriod: {},
      };
      index.set(k, entry);
    }
    const value = toNumber(row.value);
    entry.byPeriod[row.period] = {
      value,
      reason: value === null ? text(row.awaiting_reason) : null,
      reasonCode: text(row.reason_code),
      reasonDetail: text(row.reason_detail),
    };
  }
  // A line that is not live says why on every month; the latest one is kept.
  for (const entry of index.values()) {
    if (entry.state === LINE_LIVE) continue;
    const periods = Object.keys(entry.byPeriod).sort();
    const head = periods.length ? entry.byPeriod[periods[periods.length - 1]] : null;
    if (head) {
      entry.reasonCode = head.reasonCode;
      entry.reasonDetail = head.reasonDetail;
    }
  }
  return index;
}

/**
 * Lines by page state, and awaiting lines by the source they wait for, so
 * staff can quote "N of M lines live". Counts LINES, not cells. A line with
 * no state (a row written before 0093) counts as live when it holds a figure
 * in some month, else as awaiting an unnamed source.
 */
export function summarizeDerivedLines(index) {
  const out = { lines: 0, live: 0, awaiting: 0, definitionDiffers: 0, bySource: {} };
  for (const entry of index?.values?.() ?? []) {
    out.lines += 1;
    let state = entry.state;
    if (!LINE_STATES.includes(state)) {
      const any = Object.values(entry.byPeriod).some((c) => c.value !== null);
      state = any ? LINE_LIVE : LINE_AWAITING;
    }
    if (state === LINE_LIVE) out.live += 1;
    else if (state === LINE_DEFINITION_DIFFERS) out.definitionDiffers += 1;
    else {
      out.awaiting += 1;
      const source = entry.source ?? "unnamed source";
      out.bySource[source] = (out.bySource[source] ?? 0) + 1;
    }
  }
  return out;
}

/**
 * The register's "Page shows" cell for one line and month of the derived
 * page: the state, the page's figure where it shows one, and the words the
 * page shows (or, for staff, the reason behind them). Null entry: the page
 * shows nothing for the line (the board has not been rebuilt since the
 * workbook changed).
 */
export function derivedPageCell(entry, period) {
  if (!entry) {
    return { state: null, label: "Not on the page yet", value: null, reason: null };
  }
  const cell = entry.byPeriod[period] ?? null;
  const value = cell ? cell.value : null;
  if (entry.state === LINE_LIVE || (entry.state === null && value !== null)) {
    return {
      state: LINE_LIVE,
      label: "Live",
      value,
      reason: value === null ? (cell?.reason ?? "No figure for this month") : null,
    };
  }
  const label = derivedLineLabel(entry.state, entry.source) ?? AWAITING_CARD_COPY;
  const detail = entry.reasonDetail ?? cell?.reasonDetail ?? null;
  return { state: entry.state, label, value: null, reason: detail };
}
